import { loadUPCData } from "../../excel/userTools/upc";
import { tabulateUPCLaborCrews } from "../../excel/userTools/upcLaborCrews";
import { tabulateRegions } from "../../excel/userTools/regions";
import { tabulateMasterFormat, tabulateUniformat } from "../../excel/userTools/sortCodeTables";
import { tabulateUsers } from "../../excel/userTools/users";
import { savePrintLayout, type PrintLayoutSaveResult } from "../../excel/workbook/printLayout";
import { getProjectReportData, setCalculationManual, setCalculationAutomatic } from "../../excel/workbook";

import Logger from "../../clients/sentry";
import { ValidationError, handleError, serializeErrorForWarning } from "../../errors";

import { getAuthToken, getAuthUrl, getLogoutUrl, signInWithSessionToken } from "../../clients/auth";
import { revokeSessionTokens } from "../../api/auth";
import {
  flattenTableReferencingFormulasOnActiveSheet,
  type FlattenTableFormulaStats,
} from "../../excel/userTools/flattenFormulas";
import { changeCurrency, type CurrencyChangeStats, type CurrencyScope } from "../../excel/userTools/currency";
import { getTenant } from "../../utils/tenant";
import { browserStore } from "../../utils/storageKeys";

/** The sign-in result redirect.html hands back. */
interface LoginMessage {
  idToken?: string | null;
  error?: string | null;
  error_description?: string | null;
}

// Held outside the hook so a later Login click can retire the attempt an earlier one started.
let activeLogin: { cancel: () => void } | null = null;

// A sign-in window outlives a task pane reload, but the handle that closes it does not. Left behind,
// an Office dialog blocks the next one from opening at all, and a sign-in completed inside it posts
// its token to a pane that no longer has a listener. Close it while we can still reach it.
window.addEventListener("pagehide", () => activeLogin?.cancel());

/** The user closed the sign-in dialog with the X button — a back-out, not a failure. */
const DIALOG_CLOSED_BY_USER = 12006;

/**
 * Stores the token and exchanges it for an Ediphi session.
 */
const completeLogin = async (message: LoginMessage): Promise<boolean> => {
  if (!message.idToken) {
    throw new Error(message.error_description || message.error || "Sign-in failed");
  }
  browserStore.set("idToken", message.idToken);
  try {
    await getAuthToken();
  } catch (error) {
    throw handleError(error, { context: "handleLogin_getAuthToken" });
  }
  return true;
};

/**
 * Signs in through an Office dialog, the flow Microsoft documents for add-in auth: the dialog's
 * first page is on our own domain, redirects to Keycloak, and reports the result back with
 * messageParent. Office owns the window, so it always opens in front of Excel.
 */
const loginViaDialog = (authUrl: string): Promise<boolean> =>
  new Promise<boolean>((resolve, reject) => {
    // apiUrl rides along because redirect.html is a static page with no build-time environment of
    // its own, and it asks our API whether the company exists before handing off to Keycloak.
    const apiUrl = process.env.API_URL || "https://api.ediphi.com";
    const dialogUrl =
      `${window.location.origin}/redirect.html` +
      `?launchUrl=${encodeURIComponent(authUrl)}&apiUrl=${encodeURIComponent(apiUrl)}`;

    Office.context.ui.displayDialogAsync(dialogUrl, { width: 45, height: 65 }, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        reject(new Error("Could not open the sign-in window"));
        return;
      }

      const dialog = asyncResult.value;
      // Guards the two handlers against settling the same promise twice.
      let settled = false;

      activeLogin = {
        cancel: () => {
          settled = true;
          activeLogin = null;
          dialog.close();
          resolve(false);
        },
      };

      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: { message: string } | { error: number }) => {
        if (settled) return;
        settled = true;
        activeLogin = null;
        dialog.close();

        if (!("message" in arg)) {
          resolve(false);
          return;
        }
        let message: LoginMessage;
        try {
          message = JSON.parse(arg.message);
        } catch {
          reject(new Error("Invalid response from the sign-in window"));
          return;
        }
        completeLogin(message).then(resolve).catch(reject);
      });

      // Fires when the user closes the dialog with the X button, or on a navigation error. A
      // programmatic close does not fire it, so this only runs for outcomes the user never completed.
      dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg: { message: string } | { error: number }) => {
        if (settled) return;
        settled = true;
        activeLogin = null;

        // Closing the window is a deliberate back-out, not a failure, and Office has already torn
        // the dialog down.
        const errorCode = "error" in arg ? arg.error : undefined;
        if (errorCode === DIALOG_CLOSED_BY_USER) {
          resolve(false);
          return;
        }

        // A navigation error leaves the dialog standing. Since the handle is being dropped here,
        // failing to close it strands a window that blocks Office from ever opening another.
        try {
          dialog.close();
        } catch {
          // Already gone.
        }
        reject(new Error(`The sign-in window closed before login completed (${errorCode})`));
      });
    });
  });

export function useUserActions() {
  // `bypassSso` (Shift-click on the login button) appends an empty kc_idp_hint so Keycloak shows the
  // username/password form instead of auto-redirecting to the tenant's SSO IdP — used for employee login.
  const handleLogin = async (bypassSso = false): Promise<boolean> => {
    try {
      return await new Promise<boolean>((resolve, reject) => {
        const tenant = getTenant();
        if (!tenant) {
          const error = new ValidationError("No tenant found in localStorage");
          reject(error);
          return;
        }

        // A stale dialog — one that outlived the task pane it was opened from — would block Office
        // from opening another, so retire it before asking for a new one.
        activeLogin?.cancel();

        const authUrl = getAuthUrl(tenant, bypassSso);
        loginViaDialog(authUrl).then(resolve).catch(reject);
      });
    } catch (error) {
      const err = handleError(error, { context: "handleLogin" });
      Logger.error("Login failed", err);
      throw err;
    }
  };

  const handleTokenLogin = async (sessionToken: string): Promise<boolean> => {
    try {
      await signInWithSessionToken(sessionToken.replace(/^Bearer\s+/i, "").trim());
      return true;
    } catch (error) {
      const err = handleError(error, { context: "handleTokenLogin" });
      Logger.error("Login failed", err);
      throw err;
    }
  };

  const handleLogout = async () => {
    try {
      const idToken = browserStore.get("idToken");
      const tenant = getTenant();

      if (!tenant || (!idToken && !browserStore.get("xAuthToken"))) {
        console.log("Logout skipped: missing tenant or tokens");
        return;
      }

      // Revoke the server-side session tokens while xAuthToken is still present. Ediphi session
      // tokens live ~2 weeks and are never rotated, so clearing xAuthToken from localStorage alone
      // would leave a working credential behind (DO-730). Best-effort — a revoke failure must not
      // block sign-out, so log a warning and continue tearing down the local/SSO session.
      const revokeResult = await revokeSessionTokens();
      if (!revokeResult.success) {
        Logger.warn("Failed to revoke session tokens on logout", {
          error: serializeErrorForWarning(revokeResult.error),
        });
      }

      browserStore.remove("idToken");
      browserStore.remove("xAuthToken");

      // A pasted-token sign-in has no Keycloak session to end.
      if (!idToken) return;

      const logoutUrl = getLogoutUrl(tenant, idToken, window.location.href.split("#")[0]);
      console.log("Logging out...", "logoutUrl:", logoutUrl);

      // Use fetch to call the logout URL without navigating
      await fetch(logoutUrl, { method: "GET", mode: "no-cors" });
      console.log("Logged out successfully.");
    } catch (error) {
      const err = handleError(error, { context: "handleLogout" });
      Logger.error("Logout failed", err);
      throw err;
    }
  };

  const handleLoadUPC = async (): Promise<number> => {
    let regionId: string | undefined;
    let regionName: string | undefined;
    try {
      const startTime = performance.now();
      await setCalculationManual();
      const reportData = await getProjectReportData();
      regionId = reportData[0]?.region_id as string | undefined;
      regionName = reportData[0]?.region_name as string | undefined;
      await loadUPCData(regionId, regionName);
      await tabulateRegions();
      const endTime = performance.now();
      const duration = (endTime - startTime) / 1000;
      return duration;
    } catch (error) {
      const err = handleError(error, { regionId, regionName, context: "handleLoadUPC" });
      Logger.error("Load UPC data failed", err);
      throw err;
    } finally {
      await setCalculationAutomatic();
    }
  };

  const handleLoadUPCLaborCrews = async (): Promise<number> => {
    let regionId: string | undefined;
    let regionName: string | undefined;
    try {
      const startTime = performance.now();
      await setCalculationManual();
      const reportData = await getProjectReportData();
      regionId = reportData[0]?.region_id as string | undefined;
      regionName = reportData[0]?.region_name as string | undefined;
      await tabulateUPCLaborCrews(regionId, regionName);
      const endTime = performance.now();
      const duration = (endTime - startTime) / 1000;
      return duration;
    } catch (error) {
      const err = handleError(error, { regionId, regionName, context: "handleLoadUPCLaborCrews" });
      Logger.error("Load UPC labor crews failed", err);
      throw err;
    } finally {
      await setCalculationAutomatic();
    }
  };

  const handleLoadMasterFormat = async (): Promise<number> => {
    try {
      const startTime = performance.now();
      await setCalculationManual();
      await tabulateMasterFormat();
      const endTime = performance.now();
      const duration = (endTime - startTime) / 1000;
      return duration;
    } catch (error) {
      const err = handleError(error, { context: "handleLoadMasterFormat" });
      Logger.error("Load MasterFormat data failed", err);
      throw err;
    } finally {
      await setCalculationAutomatic();
    }
  };

  const handleLoadUniformat = async (): Promise<number> => {
    try {
      const startTime = performance.now();
      await setCalculationManual();
      await tabulateUniformat();
      const endTime = performance.now();
      const duration = (endTime - startTime) / 1000;
      return duration;
    } catch (error) {
      const err = handleError(error, { context: "handleLoadUniformat" });
      Logger.error("Load Uniformat data failed", err);
      throw err;
    } finally {
      await setCalculationAutomatic();
    }
  };
  const handleLoadUsers = async (): Promise<number> => {
    try {
      const startTime = performance.now();
      await setCalculationManual();
      await tabulateUsers();
      const endTime = performance.now();
      const duration = (endTime - startTime) / 1000;
      return duration;
    } catch (error) {
      const err = handleError(error, { context: "handleLoadUsers" });
      Logger.error("Load Users data failed", err);
      throw err;
    } finally {
      await setCalculationAutomatic();
    }
  };

  const handleFlattenTableFormulas = async (): Promise<FlattenTableFormulaStats> => {
    try {
      return await flattenTableReferencingFormulasOnActiveSheet();
    } catch (error) {
      const err = handleError(error, { context: "handleFlattenTableFormulas" });
      Logger.error("Flatten table formulas failed", err);
      throw err;
    }
  };
  const handleChangeCurrency = async (currencyId: string, scope: CurrencyScope): Promise<CurrencyChangeStats> => {
    try {
      return await changeCurrency(currencyId, scope);
    } catch (error) {
      const err = handleError(error, { context: "handleChangeCurrency" });
      Logger.error("Change currency failed", err);
      throw err;
    }
  };

  const handleSavePrintLayout = async (): Promise<PrintLayoutSaveResult> => {
    try {
      return await savePrintLayout();
    } catch (error) {
      const err = handleError(error, { context: "handleSavePrintLayout" });
      Logger.error("Save headers and footers failed", err);
      throw err;
    }
  };
  return {
    handleLogin,
    handleTokenLogin,
    handleLogout,
    handleLoadUPC,
    handleLoadUPCLaborCrews,
    handleLoadMasterFormat,
    handleLoadUniformat,
    handleLoadUsers,
    handleFlattenTableFormulas,
    handleChangeCurrency,
    handleSavePrintLayout,
  };
}
